import type { BookingStatus } from './bookings'
import type { OrgType } from './persona'
import type { ZoneType } from './properties'
import type { HuntType } from './hunt-type'
import type { HuntConstraintWarning } from './hunt-constraint-warning'

export type HuntTimePeriod = 'am' | 'midday' | 'pm' | 'all-day' | 'night'

export type Jurisdiction = 'none' | 'illinois' | 'saskatchewan'

export type HuntsTab = 'all' | 'mine' | 'unassigned' | 'wingid'

export type HuntLogSeasonChip = {
  label: string
  startDate: string
  endDate: string
}

export type HarvestSex = 'male' | 'female' | 'unknown'

export type HuntSpeciesBreakdown = {
  speciesId: string
  speciesName: string
  count: number
  sex: HarvestSex
  isMonomorphic: boolean
  hunterAssignments?: Record<string, number>
}

export type SpeciesSexBucket = 'drake' | 'hen' | 'unknown'

export function speciesSexBucket(
  entry: Pick<HuntSpeciesBreakdown, 'sex' | 'isMonomorphic'>,
): SpeciesSexBucket {
  if (entry.isMonomorphic) return 'unknown'
  if (entry.sex === 'male') return 'drake'
  if (entry.sex === 'female') return 'hen'
  return 'unknown'
}

export type HuntHarvestSexRollup = {
  sex: HarvestSex
  count: number
}

export type HuntHarvestSummary = {
  count: number
  rollup: HuntHarvestSexRollup[]
}

export type HuntPhoto = {
  id: string
  storagePath: string
  cloudStorageUrl?: string
  thumbnailUrl?: string
  isCoverPhoto: boolean
  caption?: string
  filesize?: number
}

export type IsoDateTime = `${string}T${string}`

export type HuntRiverGaugeReading = {
  at: IsoDateTime
  stage?: number
  flow?: number
}

export type HuntRiverGauge = {
  lid: string
  name: string
  riverName?: string
  distanceMiles?: number
  stage?: number
  stageUnits?: string
  flow?: number
  flowUnits?: string
  readings?: HuntRiverGaugeReading[]
}

export type HuntWeatherWindow = {
  start: string
  end: string
  source: 'recorded' | 'time_block' | 'default'
  timezone: string
}

export type HuntTideEventType = 'H' | 'L'

export type HuntTideEvent = {
  at: string
  type: HuntTideEventType
  heightFt: number
}

export type HuntTideStation = {
  id: string
  name: string
  distanceMiles: number
}

export type HuntTideSnapshot = {
  station: HuntTideStation
  datum: 'MLLW'
  events: HuntTideEvent[]
}

export type HuntWeather = {
  conditions: string
  tempF?: number
  windSpeedMph?: number
  windDirection?: string
  pressureInhg?: number
  precipInches?: number
  moonPhase?: number
  riverGauges?: HuntRiverGauge[]
  raw?: Record<string, unknown>
  avgTempF?: number
  minTempF?: number
  maxTempF?: number
  avgWindMph?: number
  maxWindGustMph?: number
  predominantWindDirection?: string
  predominantWindDegrees?: number
  avgPressureInHg?: number
  feelsLikeF?: number
  avgHumidityPercent?: number
  totalPrecipInches?: number
  avgCloudCoverPercent?: number
  avgVisibilityMiles?: number
  weatherSummary?: string
  window?: HuntWeatherWindow
  tides?: HuntTideSnapshot
}

export type HuntTimeBlockSource = 'location' | 'org' | 'default'

export type HuntTimeBlock = {
  name: string
  startTime: string
  endTime: string
  source: HuntTimeBlockSource
}

export type Hunt = {
  huntId: string
  huntType: HuntType
  orgId?: string
  orgType?: OrgType
  date: string
  timePeriod: HuntTimePeriod
  timeBlock?: HuntTimeBlock
  title: string
  status: string
  createdAt: string
  updatedAt: string
  memberCount: number
  guestCount: number
  memberIds: string[]
  memberNames: string[]
  memberAvatars: (string | null)[]
  memberRoles?: (string | null)[]
  totalBirds: number
  speciesCount: number
  speciesBreakdown: HuntSpeciesBreakdown[]
  harvestSummary?: HuntHarvestSummary
  memberBirdKills: Record<string, number>
  unassignedBirdsCount: number
  checkIn?: string
  checkOut?: string
  propertyId?: string
  propertyName?: string
  propertyCity?: string
  propertyState?: string
  propertyCountry?: string
  propertyTotalAcres?: number
  orgName?: string
  clubName?: string
  clubCity?: string
  clubState?: string
  clubCountry?: string
  blindId?: string
  blindName?: string
  blindLatitude?: number
  blindLongitude?: number
  blindColor?: string
  zoneId?: string
  zoneName?: string
  standId?: string
  standName?: string
  standType?: string
  standLatitude?: number
  standLongitude?: number
  locationLabel?: string
  huntingUnitLabel?: string
  locationDirectionFacing?: string
  notes?: string
  weather?: HuntWeather
  coverPhotoUrl?: string
  photos?: HuntPhoto[]
  customLocationId?: string
  customLocationName?: string
  customLocationDisplay?: string
  customLocationLatitude?: number
  customLocationLongitude?: number
  customLocationColor?: string
  customLocationWaypointType?: string
  timeIn?: string
  timeOut?: string
  crippleCount?: number
  ilSiteCode?: string
  amBlindId?: string
  pmBlindId?: string
  amCustomLocationId?: string
  pmCustomLocationId?: string
  amBlindName?: string
  pmBlindName?: string
  amCustomLocationName?: string
  pmCustomLocationName?: string
  decoySpread?: string
  spreadPatternId?: string
  decoyTypes?: string[]
  rutPhase?: string
  baitUsed?: boolean
  setup?: string
  gobblingActivity?: string
  decoyUsed?: boolean
  waterbody?: string
  managementUnit?: string
  nightMethod?: string
  method?: string
  baitedLine?: boolean
  coverType?: string
  birdsFlushed?: number
  coveysFlushed?: number
  flushesPointed?: number
  flushesWild?: number
  birdOrigin?: string
  bookingId?: string
  bookingName?: string
  bookingStatus?: BookingStatus
  jurisdiction?: Jurisdiction
}

export type Paginated<T> = {
  items: T[]
  total: number
  limit: number
  offset: number
  unassignedTotal?: number
}

export type HuntFormDataMember = {
  id: string
  displayName: string
  lastName?: string
  email?: string
  phone?: string
  isPending: boolean
  userId?: string
  role?: string
  isAdditionalHunter?: boolean
  avatarUrl?: string
}

export type HuntFormDataProperty = {
  id: string
  name: string
  flywayRegion?: string
  clubhouseLatitude?: number
  clubhouseLongitude?: number
  state?: string
  country?: string
  effectiveFolderId?: string
  folderSource?: string
  jurisdiction?: Jurisdiction
}

export type HuntFormDataBlind = {
  id: string
  name: string
  capacity?: number
  zoneId?: string
  propertyId: string
  latitude?: number
  longitude?: number
  effectiveFolderId?: string
  folderSource?: string
  isActive?: boolean
}

export type HuntFormDataZone = {
  id: string
  name: string
  propertyId: string
  zoneType?: ZoneType
  effectiveFolderId?: string
  folderSource?: string
  isActive?: boolean
}

export type HuntFormDataSpecies = {
  id: string
  name: string
  displayOrder: number
  displayOrderByFlyway?: Record<string, number>
  drakeImages: string[]
  henImages: string[]
  isMonomorphic: boolean
}

export type HuntFormDataCustomLocation = {
  id: string
  name: string
  latitude: number
  longitude: number
  address?: string
  city?: string
  state?: string
  country?: string
  waypointType?: string
  propertyId?: string
  effectiveFolderId?: string
  folderSource?: string
  jurisdiction?: Jurisdiction
  isActive?: boolean
}

export type HuntFormData = {
  members: HuntFormDataMember[]
  properties: HuntFormDataProperty[]
  blinds: HuntFormDataBlind[]
  zones: HuntFormDataZone[]
  species: HuntFormDataSpecies[]
  customLocations: HuntFormDataCustomLocation[]
  allowedHuntTypes: string[]
  jurisdiction?: Jurisdiction
}

export type CompleteHuntInput = {
  hunt: {
    huntType?: HuntType
    orgId?: string
    orgType?: OrgType
    bookingId?: string | null
    title: string
    huntDate: string
    propertyId?: string | null
    zoneId?: string | null
    blindId?: string | null
    standId?: string | null
    customLocationId?: string | null
    customLocation?: {
      name: string
      latitude: number
      longitude: number
      address?: string
      city?: string
      state?: string
      country?: string
    }
    notes?: string | null
    timePeriod?: HuntTimePeriod
    timeIn?: string | null
    timeOut?: string | null
    crippleCount?: number | null
    ilSiteCode?: string | null
    amBlindId?: string | null
    pmBlindId?: string | null
    amCustomLocationId?: string | null
    pmCustomLocationId?: string | null
    decoySpread?: string
    spreadPatternId?: string | null
    rutPhase?: string
    baitUsed?: boolean
    setup?: string
    gobblingActivity?: string
    decoyUsed?: boolean
    waterbody?: string
    managementUnit?: string
    nightMethod?: string
    method?: string
    baitedLine?: boolean
    coverType?: string
    birdsFlushed?: number
    coveysFlushed?: number
    flushesPointed?: number
    flushesWild?: number
    birdOrigin?: string
  }
  participants?: Array<{ userId?: string; membershipId?: string }>
  harvests?: Array<{
    speciesId: string
    sex: HarvestSex
    assignedUserId?: string
    assignedMembershipId?: string
    wingidAnalysisId?: string
  }>
}

export type CreateCompleteHuntInput = CompleteHuntInput & {
  hunt: Required<Pick<CompleteHuntInput['hunt'], 'huntType' | 'timePeriod'>>
}

export type MigratoryHarvestBody = {
  huntType: 'migratory_bird'
  speciesId: string
  sex: HarvestSex
  harvestDate: string
  assignedUserId?: string
  count?: number
  blindId?: string
  customLocationId?: string
  wingidAnalysisId?: string
}

export type UplandHarvestBody = {
  huntType: 'upland'
  speciesId: string
  sex: HarvestSex
  harvestDate: string
  assignedUserId?: string
  count?: number
  method?: string
  ageClass?: string
}

export type HogHarvestBody = {
  huntType: 'hog'
  speciesId: string
  sex: HarvestSex
  harvestDate: string
  assignedUserId?: string
  count?: number
}

export type BigGameHarvestBody = {
  huntType: 'deer' | 'turkey' | 'gator'
  speciesId: string
  sex: HarvestSex
  harvestDate: string
  assignedUserId?: string
  ageClass?: string
  method?: string
  antlered?: boolean
  antlerPointsLeft?: number
  antlerPointsRight?: number
  insideSpreadInches?: number
  fieldDressedWeightLbs?: number
  shotDistanceYards?: number
  trackingDistanceYards?: number
  tagNumber?: string
  beardLengthInches?: number
  beardCount?: number
  spurLengthLeftInches?: number
  spurLengthRightInches?: number
  weightLbs?: number
  totalLengthInches?: number
  tailGirthInches?: number
  captureLatitude?: number
  captureLongitude?: number
}

export type HarvestBody =
  | MigratoryHarvestBody
  | UplandHarvestBody
  | HogHarvestBody
  | BigGameHarvestBody

export type HarvestRecord = {
  id: string
  huntType: HuntType
  speciesId: string
  speciesName?: string
  sex: HarvestSex
  harvestDate: string
  assignedUserId?: string
  ageClass?: string
  method?: string
  antlered?: boolean
  antlerPointsLeft?: number
  antlerPointsRight?: number
  insideSpreadInches?: number
  fieldDressedWeightLbs?: number
  shotDistanceYards?: number
  trackingDistanceYards?: number
  tagNumber?: string
  beardLengthInches?: number
  beardCount?: number
  spurLengthLeftInches?: number
  spurLengthRightInches?: number
  weightLbs?: number
  totalLengthInches?: number
  tailGirthInches?: number
  captureLatitude?: number
  captureLongitude?: number
}

export type CompleteHuntResult = {
  huntId: string
  participantsReplaced: number
  harvestsReplaced: number
  photosDeleted: number
  photosUploaded: number
  constraintWarnings?: HuntConstraintWarning[]
}

export type AdditionalHunter = {
  id: string
  email: string
  displayName?: string
  isAdditionalHunter: boolean
}

export type ResolvedMember = {
  userId: string
  displayName: string
  membershipId?: string
  isNewUser: boolean
  isNewMembership: boolean
}
